"use client";

import Image from "next/image";
import Link from "next/link";
import styles from "./Banner.module.scss";
import assetLoader from "@utils/internalImageLoder";
import { VideoBannerConfig } from "@constants/bannerConfig";
import { EventType, trackEventWithCommonProps } from "@utils/eventTracker";
import { usePathname } from "next/navigation";
import { getPageNameFromUrl } from "@utils/pageNameUtils";

interface BannerProps {
  customClassName?: string;
  desktopBannerImage?: string;
  mobileBannerImage?: string;
  bannerLink?: string;
  bannerAlt?: string;
  type?: "image" | "video";
  videoBannerConfig?: VideoBannerConfig;
  cityName?: any;
}

export default function Banner({
  customClassName,
  desktopBannerImage,
  mobileBannerImage,
  bannerLink,
  bannerAlt = "ACKO Drive Banner",
  type = "image",
  videoBannerConfig,
  cityName
}: BannerProps) {

  const pathname = usePathname();

  const handleButtonClick = async() => {
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
        pageType: getPageNameFromUrl(pathname),
        additionalProps: {
            action_type: "tap_btn",
            action_name: "tap_btn_car_drop_banner",
            city_name: cityName,
        },
    });
  }

  if (type === "video") {
    if (!videoBannerConfig) {
      console.error("videoBannerConfig is required when type is 'video'");
      return null;
    }

    return (
      <div
        className={`${styles.ackoDriveProductContainer} ${
          customClassName ? customClassName : ""
        }`}
      >
        <Link
          className={styles.banner}
          href={videoBannerConfig.buttonLink}
          rel="noopener noreferrer"
          onClick={handleButtonClick}
        >
          <video
            className={styles.bgVideoDesktop}
            poster={videoBannerConfig.desktopThumbnailUrl}
            loop
            autoPlay
            muted
            playsInline
            disableRemotePlayback
            disablePictureInPicture
          >
            <source src={videoBannerConfig.desktopVideoUrl} />
          </video>
          <video
            className={styles.bgVideoMobile}
            poster={videoBannerConfig.mobileThumbnailUrl}
            loop
            autoPlay
            muted
            playsInline
            disableRemotePlayback
            disablePictureInPicture
          >
            <source src={videoBannerConfig.mobileVideoUrl} />
          </video>
          <div className={styles.bannerContent}>
            <div className={styles.bannerTexts}>
              {videoBannerConfig.logo && (
                <Image
                  src={videoBannerConfig.logo}
                  alt={`${bannerAlt} logo`}
                  className={styles.bannerLogo}
                  width={156}
                  height={36}
                  loading="lazy"
                />
              )}
              <div className={styles.bannerHeadingContent}>
                <div className={styles.bannerHeading}>
                  {videoBannerConfig.heading}
                </div>
                <div className={styles.bannerSubHeading}>
                  {videoBannerConfig.subHeading}
                </div>
              </div>
            </div>
            <div className={styles.bannerButton}>
              {videoBannerConfig.buttonText}
            </div>
          </div>
        </Link>
      </div>
    );
  }

  return (
    <>
      {bannerLink ? (
        <Link
          href={bannerLink}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className={`${styles.bannerContainer} ${
            customClassName ? customClassName : ""
          }`}
        >
          <Image
            src={`${assetLoader()}${desktopBannerImage}`}
            alt={bannerAlt}
            fill
            loading="lazy"
            className={styles.desktopBannerImage}
          />

          <Image
            src={`${assetLoader()}${mobileBannerImage}`}
            alt={bannerAlt}
            fill
            loading="lazy"
            className={styles.mobileBannerImage}
          />
        </Link>
      ) : (
        <div
          className={`${styles.bannerContainer} ${
            customClassName ? customClassName : ""
          }`}
        >
          <Image
            src={`${assetLoader()}${desktopBannerImage}`}
            alt={bannerAlt}
            fill
            loading="lazy"
            className={styles.desktopBannerImage}
          />

          <Image
            src={`${assetLoader()}${mobileBannerImage}`}
            alt={bannerAlt}
            fill
            loading="lazy"
            className={styles.mobileBannerImage}
          />
        </div>
      )}
    </>
  );
}
